import type { IssueContext } from '@anon-chat/shared';
import { appMode } from './app-mode';
import { getWebApp } from './telegram';

/**
 * Обстановка для обращения о неполадке.
 *
 * Собирается только то, что помогает воспроизвести поломку: экран, устройство,
 * версия сборки, последние ошибки приложения. Ни текста переписки, ни токенов,
 * ни содержимого хранилища здесь нет и быть не должно — обращение читает
 * человек, и оно не место для чужих секретов.
 */

/** Сколько последних ошибок прикладывать: больше в обращении не читают. */
const KEPT_ERRORS = 5;
/** Длина одной строки ошибки: стектрейс целиком превращает обращение в свалку. */
const ERROR_MAX_LENGTH = 300;

const recentErrors: string[] = [];

function remember(message: string): void {
  const trimmed = message.slice(0, ERROR_MAX_LENGTH);
  recentErrors.push(`${new Date().toISOString()} ${trimmed}`);
  if (recentErrors.length > KEPT_ERRORS) {
    recentErrors.shift();
  }
}

/**
 * Запоминает ошибки приложения, чтобы приложить их к обращению.
 *
 * Без них человек пишет «не работает», и починка начинается с угадывания. С
 * ними видно хотя бы, что именно упало у него в браузере.
 */
export function trackErrors(): () => void {
  const onError = (event: ErrorEvent): void => {
    remember(`${event.message} (${event.filename}:${event.lineno})`);
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason as { message?: string } | string | undefined;
    remember(typeof reason === 'string' ? reason : (reason?.message ?? 'отклонённое обещание'));
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

export function collectDiagnostics(screen: string): IssueContext {
  const app = getWebApp();

  return {
    screen,
    surface: appMode,
    appVersion: __APP_VERSION__,
    userAgent: navigator.userAgent,
    ...(app ? { telegramPlatform: app.platform, telegramVersion: app.version } : {}),
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    language: navigator.language,
    ...(recentErrors.length > 0 ? { errors: [...recentErrors] } : {}),
    occurredAt: new Date().toISOString(),
  };
}
